import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useCanvasStore, Item, Board } from '@/stores/canvasStore';
import { WallItemCard } from './WallItemCard';
import { WallFilterBar } from './WallFilterBar';
import { WallSearchWithFilters } from './WallSearchWithFilters';
import { PinnedSection } from './PinnedSection';
import { QuickAddNoteCard } from './QuickAddNoteCard';
import { WallPreviewModal } from './WallPreviewModal';
import { DropZone } from '@/components/upload/DropZone';
import { Loader2 } from 'lucide-react';
import { useWallPaste } from '@/hooks/useWallPaste';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Helper to extract searchable text from an item
const getSearchableText = (item: Item): string => {
  const parts: string[] = [];
  const content = item.content as Record<string, unknown>;
  
  // Common fields
  if (content.filename) parts.push(content.filename as string);
  if (content.title) parts.push(content.title as string);
  if (content.description) parts.push(content.description as string);
  
  // AI-generated metadata
  if (content.summary) parts.push(content.summary as string);
  if (content.analysis) parts.push(content.analysis as string);
  if (content.transcript) parts.push(content.transcript as string);
  if (content.searchText) parts.push(content.searchText as string);
  if (content.alt) parts.push(content.alt as string);
  
  // Type-specific content
  switch (item.item_type) {
    case 'sticky':
      if (content.text) parts.push(content.text as string);
      break;
    case 'text_card':
      if (content.body) parts.push(content.body as string);
      break;
    case 'document':
      if (content.content) {
        // Strip HTML tags for search
        const textContent = (content.content as string).replace(/<[^>]*>/g, '');
        parts.push(textContent);
      }
      break;
    case 'link':
      if (content.url) parts.push(content.url as string);
      if (content.siteName) parts.push(content.siteName as string);
      break;
  }
  
  return parts.join(' ');
};

export function WallView() {
  const { allItems, boards, isLoading, fetchAllItems, fetchBoards, updateItem, setWallScrollOffset, getVisibleBoards } = useCanvasStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [selectedBoardIds, setSelectedBoardIds] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showBoardFilters, setShowBoardFilters] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAllItems();
    fetchBoards();
  }, [fetchAllItems, fetchBoards]);

  // Track scroll position for TopBar animation
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      setWallScrollOffset(scrollContainerRef.current.scrollTop);
    }
  }, [setWallScrollOffset]);

  // Reset scroll offset when unmounting
  useEffect(() => {
    return () => setWallScrollOffset(0);
  }, [setWallScrollOffset]);

  // Derive pinned items reactively from allItems for instant UI updates
  const pinnedItems = useMemo(() => 
    allItems
      .filter(item => item.is_pinned_wall === true)
      .sort((a, b) => 
        new Date(b.pinned_wall_at || 0).getTime() - 
        new Date(a.pinned_wall_at || 0).getTime()
      ),
    [allItems]
  );

  // Refresh items after file upload
  const handleFilesUploaded = () => {
    fetchAllItems();
  };

  // Get target board for uploads - use first selected filter or null (no board)
  const getTargetBoardId = (): string | null => {
    if (selectedBoardIds.size > 0) {
      return Array.from(selectedBoardIds)[0];
    }
    // Return null - notes created in Wall don't need a board
    return null;
  };

  // Paste handler
  useWallPaste({
    targetBoardId: getTargetBoardId(),
    onItemCreated: fetchAllItems,
  });

  // Handle pin/unpin with optimistic updates via store
  const handlePinItem = async (itemId: string) => {
    await updateItem(itemId, { 
      is_pinned_wall: true, 
      pinned_wall_at: new Date().toISOString() 
    });
    toast.success('Pinned to Wall');
  };

  const handleUnpinItem = async (itemId: string) => {
    await updateItem(itemId, { 
      is_pinned_wall: false, 
      pinned_wall_at: null 
    });
    toast.success('Unpinned');
  };

  // FIX 5: Filter out pinned items from main grid with scored search
  const filteredItems = useMemo(() => {
    // Only show links, images, and videos in discover view (exclude text cards, stickies, documents, files)
    const DISCOVER_TYPES = new Set(['link', 'image', 'video']);
    let items = allItems.filter(item =>
      DISCOVER_TYPES.has(item.item_type) &&
      item.is_pinned_wall !== true
    );

    // Filter by selected boards
    if (selectedBoardIds.size > 0) {
      items = items.filter(item => selectedBoardIds.has(item.board_id));
    }

    // Filter by type filters
    if (typeFilters.length > 0) {
      items = items.filter(item => {
        // Special handling for videos (stored as 'video' type)
        if (typeFilters.includes('video') && item.item_type === 'video') {
          return true;
        }
        return typeFilters.includes(item.item_type);
      });
    }

    // Filter by search query with scoring
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const queryWords = q.split(/\s+/).filter(w => w.length > 1);
      
      const scored = items.map(item => {
        const searchableText = getSearchableText(item).toLowerCase();
        let score = 0;

        // Exact phrase match
        if (searchableText.includes(q)) {
          score += 100;
        }

        // Individual word matches
        for (const word of queryWords) {
          if (searchableText.includes(word)) {
            score += 10;
          }
          // Word-start matches (e.g., "des" matches "design")
          const wordStartRegex = new RegExp(`\\b${word}`, 'i');
          if (wordStartRegex.test(searchableText)) {
            score += 5;
          }
        }

        // Recency boost
        const ageMs = Date.now() - new Date(item.created_at).getTime();
        const ageDays = ageMs / (1000 * 60 * 60 * 24);
        if (ageDays < 7) score += 3;
        if (ageDays < 1) score += 5;

        return { item, score };
      });

      items = scored
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(s => s.item);
    } else {
      // Sort by most recent when no search
      items = items.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }

    return items;
  }, [allItems, selectedBoardIds, searchQuery, typeFilters]);

  const toggleBoardFilter = (boardId: string) => {
    setSelectedBoardIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(boardId)) {
        newSet.delete(boardId);
      } else {
        newSet.add(boardId);
      }
      return newSet;
    });
  };

  const getBoardForItem = (item: Item): Board | undefined => {
    return boards.find(b => b.id === item.board_id);
  };

  const handleItemUpdate = () => {
    fetchAllItems();
  };

  if (isLoading && allItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] dark:bg-[#1e1e1e] flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-gray-500 dark:text-gray-400">Loading your mind...</p>
      </div>
    );
  }

  const hasActiveSearch = searchQuery || typeFilters.length > 0;

  return (
    <DropZone mode="wall" onFilesUploaded={handleFilesUploaded}>
      <motion.div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="min-h-screen h-screen overflow-y-auto scroll-smooth bg-background text-foreground"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {/* Search Section with Filters */}
        <WallSearchWithFilters
          value={searchQuery} 
          onChange={setSearchQuery}
          activeFilters={typeFilters}
          onFiltersChange={setTypeFilters}
          resultCount={hasActiveSearch ? filteredItems.length : undefined}
          showBoardFilters={showBoardFilters}
          onToggleBoardFilters={() => setShowBoardFilters(!showBoardFilters)}
        />

        {/* Board Tag Filters - only visible when board filters toggle is enabled */}
        {/* Use all boards (not getVisibleBoards) so users can filter by Home board too */}
        <WallFilterBar
          boards={boards}
          selectedBoardIds={selectedBoardIds}
          onToggleBoardFilter={toggleBoardFilter}
          isVisible={showBoardFilters}
        />

        {/* Pinned Section */}
        <PinnedSection
          pinnedItems={pinnedItems}
          boards={boards}
          onUnpin={handleUnpinItem}
          onItemClick={setSelectedItem}
        />

        {/* Masonry Grid */}
        <div className="max-w-7xl mx-auto px-6 pb-24">
          {filteredItems.length === 0 && !hasActiveSearch ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-gray-500">
              <p className="text-lg mb-2">Your mind is empty</p>
              <p className="text-sm">Start adding notes, images, and links</p>
            </div>
          ) : filteredItems.length === 0 && hasActiveSearch ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-gray-500">
              <p className="text-lg mb-2">No results found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
              {/* Quick Add Note Card - Always first */}
              <div className="break-inside-avoid mb-5">
                <QuickAddNoteCard 
                  targetBoardId={null} 
                  onNoteCreated={fetchAllItems}
                />
              </div>
              
              {/* All items */}
              {filteredItems.map(item => (
                <div key={item.id} className="break-inside-avoid mb-5">
                  <WallItemCard
                    item={item}
                    board={getBoardForItem(item)}
                    boards={boards}
                    onClick={() => setSelectedItem(item)}
                    onPin={() => handlePinItem(item.id)}
                    onDelete={handleItemUpdate}
                    isPinned={pinnedItems.some(p => p.id === item.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview Modal with inline editing */}
        {selectedItem && (
          <WallPreviewModal
            item={selectedItem}
            board={getBoardForItem(selectedItem)}
            onClose={() => setSelectedItem(null)}
            onUpdate={handleItemUpdate}
          />
        )}
      </motion.div>
    </DropZone>
  );
}
